import { createClient } from '@supabase/supabase-js';
import { openai } from './openai';

const supabaseUrl = 'https://craewvqmwtjjsoyvrdjh.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNyYWV3dnFtd3RqanNveXZyZGpoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzgwODgwNDcsImV4cCI6MjA1MzY2NDA0N30.3G3rhrbQve-CRGs79eN4R3sEUfRhOF-S6ejtDeEC44c';

const supabase = createClient(supabaseUrl, supabaseKey);

export interface SitePage {
  id: string;
  url: string;
  title: string;
  summary: string;
  content: string;
  chunk_number: number;
  embedding: number[];
  similarity?: number;
  type?: string;
  description?: string;
}

interface ProjectUrlData {
  url: string;
  slug_grup: string;
}

export interface ProjectUrl extends ProjectUrlData {
  content_type: string;
}

const getContentTypeFromSlug = (slug_grup: string): string => {
  const typeMap: Record<string, string> = {
    'pl/artykuly': 'Article',
    'pl/uslugi': 'Service',
    'pl/wydarzenia': 'Event'
  };
  
  return typeMap[slug_grup] || 'Other';
};

// Add a new interface for combined page results
interface CombinedPage extends SitePage {
  chunks?: {
    id: string;
    chunk_number: number;
    content: string;
    similarity?: number;
  }[];
}

function deduplicateAndCombinePages(pages: SitePage[]): CombinedPage[] {
  const urlMap = new Map<string, CombinedPage>();
  
  pages.forEach(page => {
    const existingPage = urlMap.get(page.url);
    if (!existingPage) {
      // Create new entry with chunks array
      urlMap.set(page.url, {
        ...page,
        chunks: [{
          id: page.id as string,
          chunk_number: page.chunk_number,
          content: page.content,
          similarity: page.similarity
        }]
      });
    } else {
      // Add this chunk to existing page
      existingPage.chunks = existingPage.chunks || [];
      existingPage.chunks.push({
        id: page.id as string,
        chunk_number: page.chunk_number,
        content: page.content,
        similarity: page.similarity
      });
      
      // Update main page similarity if this chunk has higher similarity
      if (page.similarity && (!existingPage.similarity || page.similarity > existingPage.similarity)) {
        existingPage.similarity = page.similarity;
        existingPage.content = page.content; // Use the content from the most similar chunk
      }
      
      // Sort chunks by chunk_number
      existingPage.chunks.sort((a, b) => a.chunk_number - b.chunk_number);
    }
  });
  
  return Array.from(urlMap.values());
}

async function tryVectorSearch(
  queryEmbedding: number[],
  matchCount: number,
  similarityThreshold: number,
  timeoutMs: number
): Promise<{ data: SitePage[] | null; error: any }> {
  try {
    const result = await Promise.race([
      supabase.rpc('match_site_pages', {
        query_embedding: queryEmbedding,
        match_count: matchCount,
        similarity_threshold: similarityThreshold
      }, { 
        count: 'exact',
        head: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Vector search timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]) as { data: SitePage[] | null; error: any };

    return result;
  } catch (error) {
    if (error instanceof Error && error.message.includes('timeout')) {
      return { data: null, error };
    }
    return { data: null, error };
  }
}

export async function findRelatedContent(
  query: string,
  matchCount: number = 5,
  similarityThreshold: number = 0.3
): Promise<{
  similarContent: CombinedPage[];
}> {
  try {
    console.log('Generating embedding for query:', query);
    const embeddingResponse = await openai.embeddings.create({
      model: "text-embedding-3-small",
      input: query,
      dimensions: 1536
    });

    const queryEmbedding = embeddingResponse.data[0].embedding;
    console.log('Generated embedding of length:', queryEmbedding.length);

    // Run text search first as it's faster
    console.log('Starting text search...');
    const { data: textData, error: textError } = await supabase
      .from('site_pages')
      .select('*')
      .or(`title.ilike.%${query}%,content.ilike.%${query}%,url.ilike.%${query.toLowerCase().replace(/\s+/g, '-')}%`)
      .limit(50);
    
    let textSearchPages: CombinedPage[] = [];
    if (textError) {
      console.error('Text search failed:', textError);
    } else if (textData) {
      textSearchPages = deduplicateAndCombinePages(textData);
      console.log('Text search found:', textData.length, 'chunks,', textSearchPages.length, 'unique pages');
      console.log('Text search URLs:', textSearchPages.map(p => p.url));
    }

    // Try vector search with progressive thresholds
    let vectorSearchPages: CombinedPage[] = [];
    const attempts = [
      { timeout: 5000, threshold: similarityThreshold, count: matchCount },
      { timeout: 8000, threshold: similarityThreshold * 0.8, count: matchCount * 2 },
      { timeout: 12000, threshold: 0.1, count: matchCount * 3 }
    ];

    for (const attempt of attempts) {
      console.log(`Vector search attempt with ${attempt.timeout}ms timeout and ${attempt.threshold} threshold...`);
      const { data: vectorData, error: vectorError } = await tryVectorSearch(
        queryEmbedding,
        attempt.count,
        attempt.threshold,
        attempt.timeout
      );

      if (vectorError) {
        console.error('Vector search failed:', vectorError);
        if (textSearchPages.length > 0) {
          console.log('Using text search results as fallback');
          break;
        }
        continue;
      }

      if (vectorData && vectorData.length > 0) {
        vectorSearchPages = deduplicateAndCombinePages(vectorData);
        console.log('Vector search found:', vectorData.length, 'chunks,', vectorSearchPages.length, 'unique pages');
        console.log('Vector search URLs:', vectorSearchPages.map(p => p.url));
        break;
      }
    }

    // Combine and deduplicate results
    const allResults: CombinedPage[] = [...vectorSearchPages];
    const existingUrls = new Set(allResults.map(p => p.url));
    
    textSearchPages.forEach(p => {
      if (!existingUrls.has(p.url)) {
        allResults.push(p);
      }
    });

    console.log('Total unique pages found:', allResults.length);
    if (allResults.length > 0) {
      console.log('All found URLs:', allResults.map(p => p.url));
    }
    
    // Sort by similarity if available, otherwise keep original order
    const sortedResults = allResults.sort((a, b) => {
      if (a.similarity && b.similarity) {
        return b.similarity - a.similarity;
      }
      return 0;
    });

    return {
      similarContent: sortedResults
    };
  } catch (error) {
    console.error('Error in findRelatedContent:', error);
    return {
      similarContent: []
    };
  }
}

interface CompanyResults {
  items: any[];
  totalResults: number;
}

export const searchCompetitorSite = async (query: string, domain: string): Promise<CompanyResults> => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_SEARCH_API_KEY;
    const searchEngineId = process.env.REACT_APP_GOOGLE_SEARCH_ENGINE_ID;

    if (!apiKey || !searchEngineId) {
      throw new Error('Google Search API credentials not configured');
    }

    const cleanQuery = query.trim().replace(/\s+/g, ' ');
    const searchUrl = `https://www.googleapis.com/customsearch/v1?key=${apiKey}&cx=${searchEngineId}&q=${encodeURIComponent(cleanQuery)}&siteSearch=${domain}&siteSearchFilter=i`;

    const response = await fetch(searchUrl);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(`Google Search API error: ${data.error?.message || 'Unknown error'}`);
    }

    return {
      items: data.items || [],
      totalResults: parseInt(data.searchInformation?.totalResults) || 0
    };
  } catch (error) {
    console.error(`Error searching ${domain}:`, error);
    throw error;
  }
};

export async function findContentByKeyword(
  keyword: string,
  contentType?: string
): Promise<ProjectUrl[]> {
  try {
    const cleanKeyword = keyword.trim();
    if (!cleanKeyword) {
      console.log('Empty keyword provided, skipping search');
      return [];
    }
    
    console.log('Searching for exact keyword:', cleanKeyword);
    
    // Create URL-friendly versions of the keyword - keep phrases together
    const urlVersions = [
      cleanKeyword,
      cleanKeyword.toLowerCase(),
      cleanKeyword.toLowerCase().replace(/\s+/g, '-'),  // spaces to hyphens
      cleanKeyword.toLowerCase().replace(/\s+/g, ''),   // remove spaces
      // Add variations for article URLs
      `${cleanKeyword.toLowerCase().replace(/\s+/g, '-')}-wchodzi-w-zycie`,
      `${cleanKeyword.toLowerCase().replace(/\s+/g, '-')}-compliance`,
      // Add common URL patterns
      cleanKeyword.toLowerCase().split(/\s+/).join('-'), // kebab-case
      cleanKeyword.toLowerCase().split(/\s+/).join(''),  // no spaces
      cleanKeyword.toLowerCase().split(/\s+/).join('_'), // snake_case
    ].filter(Boolean); // Remove any empty strings
    
    // Remove duplicates
    const uniqueUrlVersions = Array.from(new Set(urlVersions));
    console.log('Searching for URL variations:', uniqueUrlVersions);
    
    // Build OR conditions for each URL version
    const urlConditions = uniqueUrlVersions
      .filter(v => v.length >= 3) // Only search for patterns of 3 or more characters
      .map(v => `url.ilike.%${v}%`)
      .join(',');
    
    if (!urlConditions) {
      console.log('No valid search patterns generated');
      return [];
    }
    
    // Log the actual SQL condition being used
    console.log('URL search condition:', urlConditions);
    
    let query = supabase
      .from('project_urls')
      .select('*')
      .or(urlConditions);

    if (contentType) {
      query = query.eq('slug_grup', `pl/${contentType.toLowerCase()}`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error in keyword search:', error);
      return [];
    }

    console.log('Keyword search found:', data?.length || 0, 'results');
    if (data?.length) {
      console.log('Found URLs:', data.map(d => d.url));
      // Log each URL with its matching pattern
      data.forEach(d => {
        const matchedPatterns = uniqueUrlVersions.filter(v => 
          v.length >= 3 && d.url.toLowerCase().includes(v.toLowerCase())
        );
        console.log('URL:', d.url, 'matched patterns:', matchedPatterns);
      });
    } else {
      // If no results, let's see what's in the database
      console.log('No results found, checking database content...');
      const { data: allUrls } = await supabase
        .from('project_urls')
        .select('url')
        .limit(10)
        .order('created_at', { ascending: false });  // Get most recent URLs
      console.log('Sample recent URLs in database:', allUrls?.map(d => d.url));
    }

    const results = (data || []).map((url: ProjectUrlData) => ({
      ...url,
      content_type: getContentTypeFromSlug(url.slug_grup)
    }));

    return results;
  } catch (error) {
    console.error('Error in findContentByKeyword:', error);
    return [];
  }
}

async function generateSearchTerms(input: string): Promise<string[]> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        {
          role: "system",
          content: `Extract the main topic keywords and generate their common variations in Polish and English.
Rules:
1. Remove contextual words like "webinar o", "zmiany w", "szkolenie na temat"
2. Include the base form of the topic
3. Include common synonyms and alternative phrasings
4. Include English equivalents if applicable
5. DO NOT include any contextual modifiers in the variations

Example Input: "webinar o zmianach w Gospodarce Obiegu Zamkniętego"
Example Output: [
  "Gospodarka Obiegu Zamkniętego",
  "Gospodarka Cyrkularna",
  "Circular Economy",
  "GOZ"
]`
        },
        {
          role: "user",
          content: input
        }
      ],
      temperature: 0.3,
    });

    const result = response.choices[0]?.message?.content;
    if (!result) return [];

    try {
      return JSON.parse(result);
    } catch (error) {
      console.error('Error parsing search terms:', error);
      return [input.trim()];
    }
  } catch (error) {
    console.error('Error generating search terms:', error);
    return [input.trim()];
  }
}

export async function suggestRelatedContent(
  briefData: {
    campaignName: string;
    primaryObjective: string;
    keyMessages: string;
    targetAudience: string;
  },
  matchCount: number = 5
): Promise<{
  similarContent: CombinedPage[];
  relatedUrls: ProjectUrl[];
}> {
  try {
    const searchInput = briefData.campaignName || briefData.keyMessages || '';
    if (!searchInput.trim()) {
      return { similarContent: [], relatedUrls: [] };
    }

    const searchTerms = await generateSearchTerms(searchInput);
    console.log('Generated search terms:', searchTerms);

    const allSimilarContent: CombinedPage[] = [];
    const allRelatedUrls: ProjectUrl[] = [];
    const seenUrls = new Set<string>();
    const exactMatches = new Set<string>();

    for (const term of searchTerms) {
      console.log('Searching for term:', term);
      
      // Text search first (exact matches)
      const { data: textData } = await supabase
        .from('site_pages')
        .select('*')
        .or(`title.ilike.%${term}%,content.ilike.%${term}%,url.ilike.%${term.toLowerCase().replace(/\s+/g, '-')}%`)
        .limit(50);
      
      if (textData && textData.length > 0) {
        const textResults = deduplicateAndCombinePages(textData);
        textResults.forEach(page => {
          if (!seenUrls.has(page.url)) {
            seenUrls.add(page.url);
            exactMatches.add(page.url); // Mark as exact match
            allSimilarContent.push({
              ...page,
              similarity: 1.0 // Give highest similarity to exact matches
            });
          }
        });
      }

      // Vector similarity search
      const { similarContent } = await findRelatedContent(term, matchCount);
      similarContent.forEach((content: CombinedPage) => {
        if (!seenUrls.has(content.url)) {
          seenUrls.add(content.url);
          allSimilarContent.push({
            ...content,
            similarity: exactMatches.has(content.url) ? 1.0 : (content.similarity || 0) * 0.8
          });
        }
      });

      // Keyword search
      const relatedUrls = await findContentByKeyword(term);
      relatedUrls.forEach(url => {
        if (!seenUrls.has(url.url)) {
          seenUrls.add(url.url);
          allRelatedUrls.push(url);
        }
      });
    }

    const sortedSimilarContent = allSimilarContent
      .sort((a, b) => {
        if (exactMatches.has(a.url) && !exactMatches.has(b.url)) return -1;
        if (!exactMatches.has(a.url) && exactMatches.has(b.url)) return 1;
        return (b.similarity || 0) - (a.similarity || 0);
      })
      .slice(0, matchCount);

    const sortedRelatedUrls = allRelatedUrls
      .sort((a, b) => {
        const aHasExactMatch = searchTerms.some(term => 
          a.url.toLowerCase().includes(term.toLowerCase().replace(/\s+/g, '-'))
        );
        const bHasExactMatch = searchTerms.some(term => 
          b.url.toLowerCase().includes(term.toLowerCase().replace(/\s+/g, '-'))
        );
        if (aHasExactMatch && !bHasExactMatch) return -1;
        if (!aHasExactMatch && bHasExactMatch) return 1;
        return 0;
      })
      .slice(0, matchCount);

    return {
      similarContent: sortedSimilarContent,
      relatedUrls: sortedRelatedUrls
    };
  } catch (error) {
    console.error('Error suggesting related content:', error);
    return {
      similarContent: [],
      relatedUrls: []
    };
  }
}

export default supabase; 